import React, { useEffect, useState } from 'react';
import { apiGet } from '../../utils/api';
import { BTN_XS } from '../../constants/buttons';
import NumberInput, { numberEntryProblem } from '../ui/NumberInput';
import { toPayloadNumber } from '../../utils/moneyPayload';

/**
 * THE PRODUCTION'S AGREEMENT — editable (owner 2026-10-28, Stage 1): "make it editable with a
 * return to defaults setting. highlight overides in another colour like yellow … If a production
 * deviates we should allow our app to deviate". Owner-approved mock: `mock-agreement.html`.
 *
 * Every number comes from the SERVER (`GET /api/productions/:id` → `timesheet_agreement_settings`,
 * built by `server/helpers/timesheetOvertime.js#agreementSettings` from the very terms the
 * calculator and pricing use): per figure its Pact/Bectu value, this production's value and
 * whether it is overridden — so the SAVED state is never re-derived here.
 *
 * CONTROLLED. The page owns the draft (`value`, null = untouched) and saves it through the ONE
 * save path, the form's Update Production (`agreementOverridesPayload` below → `PUT
 * /api/productions/:id` `timesheet_agreement_overrides`). A draft entry is the text typed, or null
 * (= Pact). Yellow = the DRAFT differs from Pact — the one comparison made here, on unsaved input.
 * `family` = 'film' | 'tv'; each family keeps its own figures (switching shows that family's).
 *
 * 🔀 STAGES 2+3 (owner 2026-10-28): the old read-only "Set elsewhere" list is now the editable
 * "Crew rates" section — the four O/T multipliers, the hourly divisor and the 6th/7th-day
 * multipliers. Crew grades FOLLOW these when blank and crew members follow their grade (Pact/Bectu
 * → production → grade → person); Budgets read the divisor and the 6th/7th. Same rows, same
 * yellow, same ↺ as every other figure here.
 */

const DAYS = [
  ['swd', 'SWD', 'Standard working day'],
  ['scwd', 'SCWD', 'Semi-continuous'],
  ['cwd', 'CWD', 'Continuous'],
  ['workshop', 'Workshop', 'Off set'],
];
// The UNIT lives in the column/row label, never after the box — so every box in a column is the
// same width at the same x (owner 2026-10-28: "columns not alligned").
const DAY_FIGURES = [
  ['standard_hours', 'Paid day', 'Paid day (h)', 'Paid day (h)'],
  ['lunch_min', 'Lunch (unpaid)', 'Lunch, unpaid (min)', 'Lunch (min)'],
  ['meal_window_hours', 'Lunch by', 'Lunch by (h after call)', 'Lunch by (h)'],
];
// 📱 Below `md` (2026-10-28 phone audit: at 390 the 3rd column sat off-screen in a 290px scroller)
// the SAME table re-flows — no second copy of the inputs, so every testid stays unique: each day is
// a block (its label on one line, then its 3 boxes in a 3-column grid, each with the 4th, SHORT
// label above it); a rule row is label · box. md+ is the table, unchanged.
const RULES = [
  ['rest_hours', 'Rest between days', 'Rest between days (h)'],
  ['broken_turnaround_multiplier', 'Broken turnaround rate', 'Broken turnaround rate (× hourly)'],
  ['broken_turnaround_cap_per_hour', 'Broken turnaround cap', 'Broken turnaround cap (£ per hour)'],
  ['ot_rounding_minutes', 'O/T rounded up to', 'O/T rounded up to (min)'],
];
// Stages 2+3: the crew figures — the level crew GRADES follow (and Budgets read the last three).
const CREW_RULES = [
  ['camera_ot_multiplier', 'Camera O/T', 'Camera O/T (× hourly)'],
  ['non_camera_ot_multiplier', 'Pre/post O/T', 'Pre/post O/T (× hourly)'],
  ['travel_multiplier', 'Travel', 'Travel (× hourly)'],
  ['meal_penalty_multiplier', 'Meal penalty', 'Meal penalty (× hourly)'],
  ['hourly_divisor', 'Hourly rate divisor', 'Hourly rate = day rate ÷'],
  ['sixth_day_multiplier', '6th day in a row', '6th day in a row (× day rate)'],
  ['seventh_day_multiplier', '7th day in a row', '7th day in a row (× day rate)'],
];
const LABELS = {
  ...Object.fromEntries(DAYS.flatMap(([k, code]) => DAY_FIGURES.map(([f, name]) => [`${k}.${f}`, `${code} ${name.toLowerCase()}`]))),
  ...Object.fromEntries(RULES.map(([k, name]) => [k, name])),
  ...Object.fromEntries(CREW_RULES.map(([k, name]) => [k, name])),
};

// Whitespace-only is BLANK too: `Number('  ')` is 0, which would send a silent zero.
const isBlank = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

/**
 * THE one "is this a readable number" rule for a figure, shared by the yellow, the payload and the
 * box's own validator (`numberEntryProblem`): `toPayloadNumber`, never `Number()` — `Number('0x8')`
 * is 8, so a dropped radix literal read "Not a number" in the box and was STORED as ×8 (2026-10-29).
 * → the number, or `null` for text it cannot read.
 */
const readFigure = (v) => toPayloadNumber(v, null);

/** Does a draft entry differ from Pact? (null/blank = Pact; a TV cap's Pact is "no cap"; unreadable = a change.) */
export function draftOverrides(entry, pact) {
  if (isBlank(entry)) return false;
  const n = readFigure(entry);
  return n === null || n !== pact;
}

/** The saved state as a draft: each overridden figure's value, every other figure null. */
export function draftFromSettings(settings) {
  const out = {};
  for (const [family, f] of Object.entries(settings.families)) {
    out[family] = Object.fromEntries(f.figures.map((x) => [x.key, x.overridden ? String(x.value) : null]));
  }
  return out;
}

/**
 * The PUT body's `timesheet_agreement_overrides` from a draft: every figure, a number or null
 * (= back to Pact). Text that is not a number is sent AS TYPED, so the server refuses it by name.
 */
export function agreementOverridesPayload(draft) {
  const out = {};
  for (const [family, figures] of Object.entries(draft)) {
    out[family] = Object.fromEntries(Object.entries(figures).map(([k, v]) => {
      if (isBlank(v)) return [k, null];
      const n = readFigure(v);
      return [k, n === null ? v : n];
    }));
  }
  return out;
}

const fmtPact = (key, pact) => (pact === null ? 'no cap' : key.endsWith('cap_per_hour') ? `£${pact}` : String(pact));

// The in-row edit control's geometry (`constants/inputs.js#ROW_EDIT_CONTROL`, colour tokens left out
// because the Pact/changed pair below owns them), at ONE fixed width, right-aligned in its cell.
// 📱 16px text below `md` so iOS does not zoom on focus.
const INPUT = 'block ml-auto w-20 max-w-full rounded-md border px-1.5 py-1 text-right text-sm max-md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const INPUT_PACT = 'border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200';
// Yellow = changed from Pact (owner). Measured in Chromium against the built CSS (2026-10-28):
// light 8.4:1, dark 10+:1. 🪤 In dark mode `index.css`'s `html.dark input { color/border !important }`
// beats the `dark:` pair below, so `sfx-input-override` (index.css) is what actually lands there.
// 🔀 EXPORTED (Stages 2+3) as THE agreement yellow: a crew GRADE / MEMBER input whose figure differs
// from the level above (`pages/BudgetCostCodes.js#OvertimeRateFields`) appends this same class — one
// yellow for one meaning, never a second shade.
export const AGREEMENT_CHANGED_INPUT_CLASS = 'sfx-input-override border-yellow-500 bg-yellow-50 font-semibold text-yellow-900 dark:border-yellow-400 dark:bg-yellow-400/10 dark:text-yellow-200';
const INPUT_CHANGED = AGREEMENT_CHANGED_INPUT_CLASS;
const CHANGED_TEXT = 'text-yellow-800 dark:text-yellow-300';
const INVALID_TEXT = 'text-red-700 dark:text-red-300';
const MUTED = 'text-xs text-gray-600 dark:text-gray-400';

export default function AgreementSummary({ productionId, family, value, onChange }) {
  const [settings, setSettings] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setSettings(null);
    setFailed(false);
    apiGet(`/api/productions/${productionId}`)
      .then(async (res) => {
        const body = res.ok ? await res.json() : null;
        if (!live) return;
        if (body && body.timesheet_agreement_settings) setSettings(body.timesheet_agreement_settings);
        else setFailed(true);
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [productionId]);

  if (!settings && !failed) return null;
  const fam = family === 'tv' ? 'tv' : 'film';
  const f = settings && settings.families && settings.families[fam];
  if (failed || !f || !Array.isArray(f.figures)) {
    return <p className={`mt-2 ${MUTED}`}>Could not load the agreement.</p>;
  }
  const draft = value || draftFromSettings(settings);
  const pactOf = Object.fromEntries(f.figures.map((x) => [x.key, x.pact]));
  const entryOf = (key) => (draft[fam] ? draft[fam][key] : null);
  const changed = (key) => draftOverrides(entryOf(key), pactOf[key]);
  const changes = f.figures.filter((x) => changed(x.key)).length;
  const setEntries = (patch) => onChange({ ...draft, [fam]: { ...draft[fam], ...patch } });
  const bounds = settings.bounds || {};
  // A figure the server did not send (an older payload) has no row — never a box with no Pact value.
  const crewRules = CREW_RULES.filter(([key]) => Object.prototype.hasOwnProperty.call(pactOf, key));

  const field = (key) => {
    const entry = entryOf(key);
    const on = changed(key);
    const pact = pactOf[key];
    const shown = isBlank(entry) ? (entry === '' ? '' : (pact === null ? '' : String(pact))) : String(entry);
    const b = bounds[key] || {};
    const label = `${LABELS[key]}${fam === 'tv' ? ' (TV)' : ' (Film)'}`;
    const control = b.choices ? (
      <select
        aria-label={label}
        data-testid={`agreement-${key}`}
        value={shown}
        onChange={(e) => setEntries({ [key]: e.target.value })}
        className={`${INPUT} ${on ? INPUT_CHANGED : INPUT_PACT}`}
      >
        {b.choices.map((c) => <option key={c} value={String(c)}>{c}</option>)}
      </select>
    ) : (
      // 🚨 `ui/NumberInput`, NEVER a bare `type="number"` (input audit HIGH 1, 2026-10-28): a bare box
      // handed `''` up for text the browser cannot read, `''` is "back to Pact", and an override
      // (Camera O/T ×2.5) was cleared on save with a 200. The marker it emits instead is sent AS
      // TYPED by `agreementOverridesPayload`, so the server refuses it by name (never `null`).
      <NumberInput
        min={b.min}
        // No server minimum → no `min` attribute at all (the old bare box rendered none).
        allowNegative={b.min == null}
        max={b.max}
        // A crew multiplier takes any step (a time-and-a-third is 1.3333) — the server says `null`.
        step={b.step == null ? 'any' : b.step}
        aria-label={label}
        testId={`agreement-${key}`}
        value={shown}
        placeholder={pact === null ? 'none' : undefined}
        onChange={(v) => setEntries({ [key]: v })}
        className={`${INPUT} ${on ? INPUT_CHANGED : INPUT_PACT}`}
      />
    );
    // A figure the box could not read: said under it, in words (the server's 400 is the block).
    const unreadable = !b.choices && numberEntryProblem(entry, { allowNegative: true }) === 'malformed';
    // The box keeps its x; a change adds a small "Pact N ↺" line UNDER it, right-aligned.
    return (
      <div className="flex flex-col items-end" data-changed={on ? 'true' : 'false'}>
        {control}
        {unreadable && (
          <span className={`mt-0.5 text-xs ${INVALID_TEXT}`} data-testid={`agreement-${key}-invalid`}>
            Not a number
          </span>
        )}
        {on && (
          <span className="mt-0.5 inline-flex items-center gap-1 whitespace-nowrap">
            <span className={`text-xs ${CHANGED_TEXT}`} data-testid={`agreement-${key}-pact`}>(Pact {fmtPact(key, pact)})</span>
            <button
              type="button"
              onClick={() => setEntries({ [key]: null })}
              aria-label={`Reset ${label} to Pact/Bectu (${fmtPact(key, pact)})`}
              title={`Back to Pact/Bectu: ${fmtPact(key, pact)}`}
              className={`px-0.5 text-sm leading-none ${CHANGED_TEXT} hover:underline max-md:inline-flex max-md:items-center max-md:justify-center max-md:min-h-[44px] max-md:min-w-[44px]`}
            >
              ↺
            </button>
          </span>
        )}
      </div>
    );
  };

  // ONE grid for both sections (`table-fixed` + the same <colgroup>, the in-row edit tables' rule):
  // a rule's value sits in the SAME column as "Paid day", every figure column the same width.
  const COLS = (
    <colgroup>
      <col />
      <col className="w-40" />
      <col className="w-40" />
      <col className="w-48" />
    </colgroup>
  );
  const th = 'px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400';
  const td = 'px-2 py-1.5 align-top text-sm text-gray-800 dark:text-gray-200';
  const rowRule = 'border-t border-gray-200 dark:border-gray-700';
  return (
    <div data-testid="agreement-summary" className="mt-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{fam === 'tv' ? 'TV' : 'Film'} agreement — this production</p>
          {changes > 0 && (
            <span data-testid="agreement-changes" className="rounded-full border border-yellow-500 bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-900 dark:border-yellow-400 dark:text-yellow-200">
              ⚑ {changes} change{changes === 1 ? '' : 's'} from Pact/Bectu
            </span>
          )}
        </div>
        <button
          type="button"
          disabled={changes === 0}
          onClick={() => setEntries(Object.fromEntries(f.figures.map((x) => [x.key, null])))}
          className={`${BTN_XS.secondary} max-md:min-h-[44px] disabled:cursor-default disabled:opacity-50`}
        >
          ↺ Reset all to Pact/Bectu
        </button>
      </div>
      <p className={`mt-1 ${MUTED}`}>
        Starts as the Pact/Bectu {fam === 'tv' ? 'Scripted TV Agreement' : 'Major Motion Picture terms'}. Change any figure this production has agreed differently — a change shows yellow, with the agreement value beside it.
      </p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full md:min-w-[34rem] max-w-[48rem] table-fixed max-md:block" data-testid="agreement-grid">
          {COLS}
          <thead className="max-md:hidden">
            <tr>
              <th className={`${th} text-left`}>Day</th>
              {DAY_FIGURES.map(([fig, , head]) => <th key={fig} className={`${th} text-right`}>{head}</th>)}
            </tr>
          </thead>
          <tbody className="max-md:block">
            {DAYS.map(([k, code, name]) => (
              <tr key={k} className={`${rowRule} max-md:grid max-md:grid-cols-3 max-md:gap-x-2 max-md:pb-2`} data-testid={`agreement-day-${k}`}>
                <td className={`${td} pt-2.5 max-md:col-span-3 max-md:px-0 max-md:pb-1`}>
                  <span className="font-medium">{code}</span> <span className={MUTED}>{name}{k === 'workshop' ? ' · lunch by after start' : ''}</span>
                </td>
                {DAY_FIGURES.map(([fig, , , phone]) => (
                  <td key={fig} className={`${td} max-md:block max-md:px-0 max-md:py-0`}>
                    <span className={`md:hidden block text-right leading-tight mb-0.5 ${MUTED}`} aria-hidden="true">{phone}</span>
                    {field(`${k}.${fig}`)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="max-md:block">
              <th colSpan={4} className={`${th} pt-4 text-left text-sm text-gray-900 dark:text-gray-100 max-md:block max-md:px-0`}>Rest and overtime</th>
            </tr>
            {RULES.map(([key, , label]) => (
              <tr key={key} className={`${rowRule} max-md:grid max-md:grid-cols-[1fr_auto] max-md:gap-x-2`}>
                <td className={`${td} pt-2.5 max-md:px-0`}>{label}</td>
                <td className={`${td} max-md:pr-0`}>{field(key)}</td>
                <td className={`${td} max-md:hidden`} />
                <td className={`${td} max-md:hidden`} />
              </tr>
            ))}
            {crewRules.length > 0 && (
              <tr className="max-md:block">
                <th colSpan={4} className={`${th} pt-4 text-left text-sm text-gray-900 dark:text-gray-100 max-md:block max-md:px-0`}>Crew rates</th>
              </tr>
            )}
            {crewRules.length > 0 && (
              <tr className="max-md:block">
                <td colSpan={4} className={`${td} pt-0 max-md:block max-md:px-0`} data-testid="agreement-crew-note">
                  <span className={MUTED}>Crew grades follow these unless a grade sets its own (Budget → Crew Grades); crew follow their grade. Budgets use the hourly divisor and the 6th / 7th day.</span>
                </td>
              </tr>
            )}
            {crewRules.map(([key, , label]) => (
              <tr key={key} className={`${rowRule} max-md:grid max-md:grid-cols-[1fr_auto] max-md:gap-x-2`}>
                <td className={`${td} pt-2.5 max-md:px-0`}>{label}</td>
                <td className={`${td} max-md:pr-0`}>{field(key)}</td>
                <td className={`${td} max-md:hidden`} />
                <td className={`${td} max-md:hidden`} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={`mt-2 ${MUTED}`}>
        Saved with Update Production. Changes apply to timesheet weeks not yet submitted and to budget versions not yet locked; a submitted week, or a locked version, keeps the figures it was priced with. Every change is recorded (who, when, old → new).
      </p>
    </div>
  );
}
